// ЗАДАЧА B: Универсальный колбэк аутентификации
// Поддерживает оба сценария: ?code= и #access_token
// Дождается появления session.user перед редиректом
import { InlineLoadingIndicator } from '@/components/ui/inline-loading';
import { SFIcon } from '@/components/ui/sf-icon';
import { Heading } from "@/components/ui/typography";
import { isAdminDomain } from '@/lib/domains/admin';
import { normalizeNextRoute } from '@/lib/routing/onboardingRouteMap';
import { getOnboarding, isAuthRetryableError } from '@/lib/rpc/user/onboarding';
import { sfIcons } from '@/lib/sf-icons';
import { supabaseBrowser } from '@/lib/supabase-browser';
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

type AuthStatus = 'processing' | 'success' | 'error';

interface AuthCallbackProps {
  redirectTo?: string;
  isAdminFlow?: boolean;
}

const supabase = supabaseBrowser;

function safeParse<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

type SupabaseSession = Awaited<ReturnType<typeof supabase.auth.getSession>>['data']['session'];

function mapAuthErrorMessage(error: unknown): string {
  const rawMessage =
    typeof error === 'object' && error !== null && 'message' in error
      ? String((error as { message?: unknown }).message ?? '')
      : typeof error === 'string'
        ? error
        : '';
  const normalized = rawMessage.toLowerCase();

  if (
    normalized.includes('otp') ||
    normalized.includes('token') ||
    normalized.includes('link')
  ) {
    if (
      normalized.includes('expired') ||
      normalized.includes('invalid') ||
      normalized.includes('used') ||
      normalized.includes('timeout')
    ) {
      return 'This link is no longer valid. Request a new one.';
    }
  }

  if (normalized.includes('session timeout')) {
    return 'This link is no longer valid. Request a new one.';
  }

  return rawMessage || 'Authentication failed. Please request a new link.';
}

export default function UniversalAuthCallback({ redirectTo = '/admin', isAdminFlow = false }: AuthCallbackProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AuthStatus>('processing');
  const [message, setMessage] = useState('Signing you in...');

  useEffect(() => {
    let mounted = true;

    const processAuth = async () => {
      try {
        setStatus('processing');
        setMessage('Signing you in...');

        const url = new URL(window.location.href);
        const codeParam = url.searchParams.get('code');
        const tokenHash = url.searchParams.get('token_hash');
        const otpType = url.searchParams.get('type');
        const emailParam = url.searchParams.get('email');
        const hashParams = url.hash ? new URLSearchParams(url.hash.substring(1)) : null;

        if (codeParam) {
          const { error } = await supabase.auth.exchangeCodeForSession(codeParam);
          if (error) throw error;
        } else if (tokenHash && otpType) {
          const storedEmail = (() => {
            try {
              return window.localStorage.getItem('pending_email');
            } catch {
              return null;
            }
          })();
          const email = emailParam ?? storedEmail ?? undefined;
          if (!email) {
            throw new Error('Missing email for one-time password verification.');
          }
          const { error } = await supabase.auth.verifyOtp({
            type: otpType as any,
            token_hash: tokenHash,
            email,
          });
          if (error) throw error;
          try {
            window.localStorage.removeItem('pending_email');
          } catch {
            /* ignore */
          }
        } else if (hashParams?.get('access_token') && hashParams?.get('refresh_token')) {
          const access_token = hashParams.get('access_token')!;
          const refresh_token = hashParams.get('refresh_token')!;
          const { error } = await supabase.auth.setSession({ access_token, refresh_token });
          if (error) throw error;
        }

        let sessionFound = false;
        let sessionUser: SupabaseSession | null = null;

        for (let i = 0; i < 15; i++) {
          if (!mounted) return; // Component unmounted

          const { data } = await supabase.auth.getSession();
          if (data.session?.user) {
            console.log(`[UniversalAuthCallback] Session found after ${i * 100}ms`);
            sessionFound = true;
            sessionUser = data.session;
            break;
          }
          await new Promise(r => setTimeout(r, 100));
        }

        if (!mounted) return;

        if (!sessionFound) {
          throw new Error('Session timeout - please try again');
        }

        if (isAdminDomain()) {
          // Check if redirect_to is external URL (cross-domain auth)
          const urlParams = new URLSearchParams(window.location.search);
          const redirectToParam = urlParams.get('redirect_to');

          if (redirectToParam && redirectToParam.startsWith('http')) {
            // External redirect - append access_token for cross-domain auth
            console.log(`[UniversalAuthCallback] External redirect detected: ${redirectToParam}`);

            const { data } = await supabase.auth.getSession();
            const accessToken = data.session?.access_token;

            queryClient.clear();
            setStatus('success');
            setMessage('Redirecting to external service...');

            const redirectUrl = new URL(redirectToParam);
            if (accessToken) {
              redirectUrl.searchParams.set('access_token', accessToken);
            }

            setTimeout(() => {
              if (!mounted) return;
              window.location.href = redirectUrl.toString();
            }, 300);
            return;
          }

          // Internal redirect (same SPA)
          const adminDestination = redirectTo || '/admin';
          console.log(`[UniversalAuthCallback] Session established (admin) → redirect: ${adminDestination}`);

          queryClient.clear();
          setStatus('success');
          setMessage('Admin session established. Redirecting…');

          setTimeout(() => {
            if (!mounted) return;
            navigate(adminDestination, { replace: true });
          }, 300);
          return;
        }

        const ensureActiveSession = async () => {
          const { data } = await supabase.auth.getSession();
          if (!data.session) {
            throw new Error('Session timeout - please try again');
          }
          return data.session;
        };

        const ensureSessionWithJwt = async () => {
          let activeSession = await ensureActiveSession();
          if (activeSession.access_token) {
            return activeSession;
          }

          const { error: refreshError } = await supabase.auth.refreshSession();
          if (refreshError) {
            throw refreshError;
          }

          activeSession = await ensureActiveSession();
          if (!activeSession.access_token) {
            throw new Error('[auth] no JWT after refresh');
          }

          return activeSession;
        };

        sessionUser = await ensureSessionWithJwt();

        const logGeoActivity = async () => {
          if (typeof window === 'undefined' || !sessionUser?.user?.id) {
            return;
          }

          const getGeoPayload = <T,>(key: string): T | null => {
            let value: T | null = null;
            try {
              value = safeParse<T>(window.sessionStorage.getItem(key));
            } catch {
              value = null;
            }
            if (!value) {
              try {
                value = safeParse<T>(window.localStorage.getItem(key));
              } catch {
                value = null;
              }
            }
            return value;
          };

          const disclosed = getGeoPayload<{
            country?: string | null;
            city?: string | null;
            restriction_tier?: string | null;
            restriction_level?: string | null;
            vpn_detected?: boolean;
            session_id?: string | null;
            timestamp?: string;
          }>('geo_disclosed');

          const background = getGeoPayload<{
            country?: string | null;
            city?: string | null;
            restriction_tier?: string | null;
            vpn?: boolean;
            restrictions?: { restriction_tier?: string | null };
            session_id?: string | null;
            timestamp?: string;
          }>('geo_background');

          const sessionId =
            disclosed?.session_id ??
            background?.session_id ??
            null;

          const detectedCountry =
            disclosed?.country ??
            background?.country ??
            null;

          const restrictionTier =
            disclosed?.restriction_tier ??
            background?.restriction_tier ??
            background?.restrictions?.restriction_tier ??
            null;

          const vpnDetected =
            disclosed?.vpn_detected ??
            background?.vpn ??
            false;

          const userAgent =
            typeof navigator !== 'undefined' ? navigator.userAgent : null;

          try {
            await supabase.rpc('log_geo_event', {
              p_user_id: sessionUser.user.id,
              p_event_type: 'login_success',
              p_detected_country: typeof detectedCountry === 'string' ? detectedCountry : null,
              p_effective_country: typeof detectedCountry === 'string' ? detectedCountry : null,
              p_provider_name: 'supabank',
              p_provider_service: isAdminFlow ? 'admin_auth' : 'user_auth',
              p_restriction_tier: restrictionTier ?? null,
              p_user_action: disclosed ? 'acknowledged' : (vpnDetected ? 'vpn_detected' : 'implicit'),
              p_resolution_type: 'allowed',
              p_session_id: sessionId,
              p_user_agent: userAgent
            });
          } catch (geoError) {
            console.warn('[UniversalAuthCallback] Failed to log geo activity', geoError);
          } finally {
            try {
              window.sessionStorage.removeItem('geo_disclosed');
              window.sessionStorage.removeItem('geo_background');
              window.localStorage.removeItem('geo_disclosed');
              window.localStorage.removeItem('geo_background');
            } catch {
              /* ignore storage cleanup errors */
            }
          }
        };

        await logGeoActivity();

        const fetchOnboarding = async () => getOnboarding(supabase);

        const onboarding = await (async () => {
          try {
            return await fetchOnboarding();
          } catch (error: any) {
            const message = String(error?.message ?? error ?? '');
            if (message.includes('onboarding_user') && isAuthRetryableError(error)) {
              await new Promise(resolve => setTimeout(resolve, 150));
              await ensureSessionWithJwt();
              return await fetchOnboarding();
            }
            throw error;
          }
        })();

        const canonicalRoute = normalizeNextRoute(onboarding.next_route);
        const destination = canonicalRoute || redirectTo;

        console.log(`[UniversalAuthCallback] Session established (www) → redirect: ${destination}`);

        queryClient.clear();
        setStatus('success');
        setMessage('Authentication successful! Redirecting...');

        setTimeout(() => {
          if (!mounted) return;
          navigate(destination, { replace: true });
        }, 800);

      } catch (error: any) {
        console.error('[UniversalAuthCallback] Auth failed:', error);

        if (!mounted) return;

        setStatus('error');
        setMessage(mapAuthErrorMessage(error));
      }
    };

    processAuth();

    return () => {
      mounted = false;
    };
  }, [navigate, queryClient, redirectTo]);

  const getStatusIcon = () => {
    switch (status) {
      case 'processing':
        return (
          <InlineLoadingIndicator
            size="lg"
            className="text-primary"
            aria-label="Processing authentication"
          />
        );
      case 'success':
        return <SFIcon icon={sfIcons.success} className="h-8 w-8 text-success" />;
      case 'error':
        return <SFIcon icon={sfIcons.warning} className="h-8 w-8 text-destructive" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'processing':
        return 'text-primary';
      case 'success':
        return 'text-success';
      case 'error':
        return 'text-destructive';
    }
  };

  return (
    <div className={cn("min-h-screen flex items-center justify-center", semanticTokens.bg.canvas)}>
      <div className="max-w-md w-full space-y-8 p-8">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            {getStatusIcon()}
          </div>

          <Heading level={2} className={cn("mb-2", semanticTokens.fg.default)}>
            {isAdminFlow ? 'Admin Authentication' : 'Authentication'}
          </Heading>

          <p className={`text-lg ${getStatusColor()}`}>
            {message}
          </p>

          {status === 'error' && (
            <div className="mt-6 space-y-4">
              <button
                onClick={() => navigate('/auth')}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Try Again
              </button>

              <p className={cn("text-sm", semanticTokens.fg.muted)}>
                If the problem persists, please contact support.
              </p>
            </div>
          )}

          {status === 'processing' && (
            <div className="mt-6">
              <div className="bg-primary/5 border border-primary/20 rounded-md p-4">
                <div className="flex items-start gap-2 text-sm text-primary">
                  <SFIcon icon={sfIcons.lock} className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div>
                    Processing your authentication...
                    <span className="block text-xs mt-1">
                      This may take a few seconds.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}